import React, { useState, useEffect } from "react";
import axios from "axios";
import Card from "components/card";
import { readToken } from "utils/session";
import DeviceSelect from "components/DeviceSelect";

const Configure = () => {
  const [configurations, setConfigurations] = useState([]);
  const [activeConfig, setActiveConfig] = useState(null);
  const [formData, setFormData] = useState({});
  
  // Device Selection State
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState("");

  // UI States
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ type: "", message: "" });

  const userKey = readToken();
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

  // 1. Fetch Devices and Configurations on Mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch Fleet List for the Dropdown
        const fleetConfig = {
          method: "get",
          url: `${apiBaseUrl}/api/fleetlist`,
          headers: { Authorization: `Bearer ${userKey}` },
        };
        const fleetRes = await axios.request(fleetConfig);
        if (fleetRes.data.fleetlist) {
          setDevices(fleetRes.data.fleetlist);
          if (fleetRes.data.fleetlist.length > 0) {
            setSelectedDevice(fleetRes.data.fleetlist[0].did);
          }
        }

        // Fetch Configuration Schemas
        const configReq = {
          method: "get",
          url: `${apiBaseUrl}/api/getconfigurations`,
          headers: { Authorization: `Bearer ${userKey}` },
        };
        const configRes = await axios.request(configReq);
        const configList = configRes.data?.reportData?.configurations || [];
        
        setConfigurations(configList);
        
        // Set the first tab as active by default
        if (configList.length > 0) {
          handleTabChange(configList[0]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setFeedback({ type: "error", message: "Failed to load configurations or fleet list." });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [apiBaseUrl, userKey]);

  // 2. Handle Tab Change (Reset form data to defaults of the new tab)
  const handleTabChange = (config) => {
    setActiveConfig(config);
    setFeedback({ type: "", message: "" });
    
    // Initialize form state with default values from the schema
    const initialData = {};
    config.fields.forEach((field) => {
      if (field.name) {
        initialData[field.name] = field.defaultValue !== undefined ? field.defaultValue : "";
      }
    });
    setFormData(initialData);
  };

  // 3. Handle Input Changes
  const handleInputChange = (e, field) => {
    const { name, value, type, checked } = e.target;
    
    let newValue = value;
    if (type === "checkbox" || field.type === "toggle") {
      newValue = checked;
    } else if (field.type === "numericStepper" || field.type === "numberInput" || field.type === "slider") {
      newValue = value === "" ? "" : Number(value);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  // 4. Handle Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDevice) {
      setFeedback({ type: "error", message: "Please select a device first." });
      return;
    }

    setFeedback({ type: "", message: "" });
    setIsSubmitting(true);

    const payload = {
      config_id: activeConfig.id,
      settings: formData,
      device_id: String(selectedDevice),
    };

    try {
      const submitReq = {
        method: "post",
        url: `${apiBaseUrl}/api/v1/submit_config`,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userKey}`,
        },
        data: payload,
      };

      await axios.request(submitReq);
      setFeedback({ type: "success", message: "Configuration saved successfully!" });
      
      // Clear success message after 3 seconds
      setTimeout(() => setFeedback({ type: "", message: "" }), 3000);
    } catch (error) {
      console.error("Submit error:", error);
      setFeedback({ type: "error", message: "Failed to save configuration." });
    } finally {
      setIsSubmitting(false);
    }
  };

  // 5. Dynamic Field Renderer
  const renderField = (field) => {
    const value = formData[field.name];

    switch (field.type) {
      case "static":
        return (
          <div className="rounded-md bg-orange-50 p-4 border border-orange-200 dark:bg-navy-800 dark:border-navy-600">
            <p className="text-sm text-orange-800 dark:text-orange-200">{field.text}</p>
          </div>
        );

      case "text":
      case "password":
      case "time":
        return (
          <input
            type={field.subtype || field.type === "time" ? "time" : "text"}
            name={field.name}
            value={value || ""}
            onChange={(e) => handleInputChange(e, field)}
            placeholder={field.placeholder}
            required={field.required === "true" || field.required === true}
            maxLength={field.validation?.maxLength}
            minLength={field.validation?.minLength}
            className="w-full rounded-md border border-gray-200 bg-white p-2.5 text-sm outline-none focus:border-brand-500 dark:border-navy-600 dark:bg-navy-900 dark:text-white"
          />
        );

      case "numericStepper":
      case "numberInput":
        return (
          <input
            type="number"
            name={field.name}
            value={value !== undefined ? value : ""}
            onChange={(e) => handleInputChange(e, field)}
            min={field.min || field.validation?.min}
            max={field.max || field.validation?.max}
            step={field.step || field.validation?.step || 1}
            required={field.required === "true" || field.required === true}
            className="w-full rounded-md border border-gray-200 bg-white p-2.5 text-sm outline-none focus:border-brand-500 dark:border-navy-600 dark:bg-navy-900 dark:text-white"
          />
        );

      case "textarea":
        return (
          <textarea
            name={field.name}
            value={value || ""}
            onChange={(e) => handleInputChange(e, field)}
            placeholder={field.placeholder}
            rows={field.rows || 3}
            maxLength={field.validation?.maxLength}
            className="w-full rounded-md border border-gray-200 bg-white p-2.5 text-sm outline-none focus:border-brand-500 dark:border-navy-600 dark:bg-navy-900 dark:text-white"
          />
        );

      case "dropdown":
        return (
          <select
            name={field.name}
            value={value || ""}
            onChange={(e) => handleInputChange(e, field)}
            className="w-full rounded-md border border-gray-200 bg-white p-2.5 text-sm outline-none focus:border-brand-500 dark:border-navy-600 dark:bg-navy-900 dark:text-white"
          >
            {field.options?.map((opt, idx) => (
              <option key={idx} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        );

      case "radioGroup":
        return (
          <div className="flex flex-col gap-2">
            {field.options?.map((opt, idx) => (
              <label key={idx} className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-white">
                <input
                  type="radio"
                  name={field.name}
                  value={opt.value}
                  checked={value === opt.value}
                  onChange={(e) => handleInputChange(e, field)}
                  className="h-4 w-4 text-brand-500"
                />
                {opt.label}
              </label>
            ))}
          </div>
        );

      case "toggle":
      case "checkbox":
        return (
          <label className="flex items-center gap-3 cursor-pointer">
            <div className="relative">
              <input
                type="checkbox"
                name={field.name}
                checked={!!value}
                onChange={(e) => handleInputChange(e, field)}
                className="peer sr-only"
              />
              {/* Custom Toggle Switch UI */}
              <div className="h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-brand-500 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none dark:bg-navy-600 dark:peer-checked:bg-brand-400"></div>
            </div>
            <span className="text-sm text-gray-700 dark:text-white">{field.label}</span>
          </label>
        );

      case "slider":
        return (
          <div className="flex flex-col gap-2">
            <input
              type="range"
              name={field.name}
              value={value || 0}
              min={field.min}
              max={field.max}
              step={field.step}
              onChange={(e) => handleInputChange(e, field)}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-navy-600 accent-brand-500"
            />
            <div className="text-right text-xs font-bold text-gray-600 dark:text-gray-300">
              Value: {value}
            </div>
          </div>
        );

      default:
        return <p className="text-sm text-red-500">Unsupported field type: {field.type}</p>;
    }
  };

  if (isLoading) {
    return <div className="mt-10 text-center font-medium text-gray-600 dark:text-white">Loading configurations...</div>;
  }

  return (
    <div className="mt-5 flex h-full w-full flex-col gap-5">
      
      {/* Top Bar: Device Selector */}
      <Card extra="w-full p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-navy-700 dark:text-white">Device Configuration</h2>
          <p className="text-sm text-gray-500">Select an asset to update its parameters.</p>
        </div>
        <DeviceSelect
          devices={devices}
          value={selectedDevice ? [selectedDevice] : []}
          onChange={(ids) => setSelectedDevice(ids[0] || "")}
          className="sm:min-w-[18rem]"
        />
      </Card>

      {/* Main Split Layout */}
      <div className="flex flex-col gap-5 lg:flex-row">
        
        {/* Left Sidebar: Configuration Tabs */}
        <Card extra="w-full lg:w-1/3 p-4 h-fit">
          <h3 className="mb-4 text-lg font-bold text-navy-700 dark:text-white">Settings Menu</h3>
          <div className="flex flex-col gap-2">
            {configurations.map((config) => (
              <button
                key={config.id}
                onClick={() => handleTabChange(config)}
                className={`flex w-full flex-col items-start rounded-lg px-4 py-3 text-left transition-colors ${
                  activeConfig?.id === config.id
                    ? "bg-brand-500 text-white shadow-md dark:bg-brand-400"
                    : "bg-lightPrimary text-gray-700 hover:bg-gray-100 dark:bg-navy-900 dark:text-white dark:hover:bg-navy-800"
                }`}
              >
                <span className="font-bold">{config.displayName}</span>
                <span className={`text-xs mt-1 ${activeConfig?.id === config.id ? "text-brand-50" : "text-gray-500"}`}>
                  {config.description.length > 50 ? `${config.description.substring(0, 50)}...` : config.description}
                </span>
              </button>
            ))}
          </div>
        </Card>

        {/* Right Area: Dynamic Form */}
        <Card extra="w-full lg:w-2/3 p-6">
          {activeConfig ? (
            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
              <div className="border-b border-gray-200 pb-4 dark:border-navy-600">
                <h3 className="text-xl font-bold text-navy-700 dark:text-white">{activeConfig.displayName}</h3>
                <p className="mt-1 text-sm text-gray-500">{activeConfig.description}</p>
              </div>

              {/* Form Fields Mapping */}
              <div className="flex flex-col gap-5">
                {activeConfig.fields.map((field, index) => (
                  <div key={index} className="flex flex-col gap-1">
                    {field.type !== "toggle" && field.type !== "checkbox" && field.type !== "static" && (
                      <label className="text-sm font-bold text-navy-700 dark:text-white">
                        {field.label} {field.required && <span className="text-red-500">*</span>}
                      </label>
                    )}
                    
                    {renderField(field)}
                    
                    {field.helperText && (
                      <p className="mt-1 text-xs text-gray-500">{field.helperText}</p>
                    )}
                  </div>
                ))}
              </div>

              {/* Form Actions Mapping */}
              <div className="mt-4 flex items-center gap-4 border-t border-gray-200 pt-6 dark:border-navy-600">
                {activeConfig.actions?.map((action, idx) => {
                  if (action.type === "submit") {
                    return (
                      <button
                        key={idx}
                        type="submit"
                        disabled={isSubmitting}
                        className="rounded-md bg-brand-500 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-brand-600 disabled:opacity-50"
                      >
                        {isSubmitting ? "Saving..." : action.label}
                      </button>
                    );
                  }
                  if (action.type === "reset") {
                    return (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => handleTabChange(activeConfig)} // Reset to default
                        className="rounded-md border border-gray-200 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-navy-600 dark:bg-navy-900 dark:text-white dark:hover:bg-navy-800"
                      >
                        {action.label}
                      </button>
                    );
                  }
                  return null;
                })}
              </div>

              {/* Feedback Messages */}
              {feedback.message && (
                <div className={`rounded-md p-3 text-sm font-bold ${feedback.type === 'success' ? 'bg-green-50 text-green-600 border border-green-200' : 'bg-red-50 text-red-600 border border-red-200'}`}>
                  {feedback.message}
                </div>
              )}
            </form>
          ) : (
            <div className="flex h-full items-center justify-center text-gray-500">
              Select a configuration from the menu.
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Configure;